import { getMicPreset, MIN_TRANSCRIPT_CHARS } from './micSensitivity';

// ======================================================
// REALTIME EVENT HANDLING (GA API)
// ======================================================
class RealtimeEventHandler {
  constructor(statusDisplay, emitEvent, toolHandler) {
    this.statusDisplay = statusDisplay;
    this.emitEvent = emitEvent;
    this.toolHandler = toolHandler;
    this.lastMsgObj = null;
    this.heygenManager = null; // set by RealtimeTourGuide.initHeyGen()
    this.audioBridge = null;   // set by RealtimeTourGuide.initHeyGen() in LITE mode
    this.send = null;          // set by RealtimeTourGuide — sends a client event on the data channel
    this._t_speechStop = null;      // ASR timing only (cleared once the transcript lands)
    // Per-turn latency tracking. A turn starts at speech_stopped and covers
    // every response created after it: on knowledge lookups that is the short
    // "Let me check." + tool-call response, then the post-tool answer.
    this._turn = null;              // { t0, llmSent, e2eSent, responses:Set }
    this._respFirstDelta = {};      // response_id → performance.now() of first delta
    this._toolResponses = new Set();// response_ids that ended in a function call
    this._respTranscripts = {};     // response_id → assistant text (emitted on response.done)
    this._awaitingAnswerSince = null;
    this.micPreset = getMicPreset();
    this._responseActive = false;
    this._bargeInTimer = null;
    this._bargedIn = false;
    this._fillerIds = new Set();    // response_ids of out-of-band "one moment" lines
    this._filler = null;            // { done:Promise, resolve, id, speaking } while one is pending
    this._cancelFiller = false;     // filler dropped before its response.created arrived
    if (toolHandler) toolHandler.events = this;
  }

  // Out-of-band "one moment" line, asked for by ToolHandler while a slow
  // knowledge lookup runs. conversation:'none' keeps it out of the history;
  // the answer waits for it (waitForFiller) so the two never overlap.
  sayFiller(instructions) {
    if (!this.send || this._filler) return;
    let resolve;
    const done = new Promise(r => { resolve = r; });
    this._filler = { done, resolve, id: null };
    setTimeout(() => this._endFiller(), 4000); // never hold the answer back for long
    this.send({
      type: 'response.create',
      response: { conversation: 'none', tool_choice: 'none', instructions, metadata: { purpose: 'filler' } },
    });
  }

  // The answer is ready. A filler the visitor hasn't heard yet is cancelled
  // rather than waited for; one already being spoken is let finish.
  waitForFiller() {
    const f = this._filler;
    if (!f) return Promise.resolve();
    if (f.speaking) return f.done;
    if (f.id) this.send({ type: 'response.cancel', response_id: f.id });
    else this._cancelFiller = true;
    this._endFiller();
    return Promise.resolve();
  }

  _endFiller() {
    const f = this._filler;
    if (!f) return;
    this._filler = null;
    f.resolve();
  }

  setMicPreset(preset) {
    this.micPreset = preset || getMicPreset();
  }

  // Stop the avatar talking: local playback plus, when OpenAI does not
  // interrupt on its own (manual turns), the server-side response.
  _interruptAvatar() {
    if (this.audioBridge) this.audioBridge.abort();
    if (this.heygenManager) this.heygenManager.interrupt();
    if (this.micPreset.manualTurns && this._responseActive && this.send) {
      this.send({ type: 'response.cancel' });
      this.send({ type: 'output_audio_buffer.clear' });
    }
  }

  // Visitor started speaking. Short sounds (noise, a cough) shorter than
  // bargeInMs don't cut the avatar off; real speech does.
  _onSpeechStarted() {
    clearTimeout(this._bargeInTimer);
    this._bargedIn = false;
    const delay = this.micPreset.bargeInMs || 0;
    if (!delay) {
      this._bargedIn = true;
      this._interruptAvatar();
      return;
    }
    this._bargeInTimer = setTimeout(() => {
      this._bargedIn = true;
      this._interruptAvatar();
    }, delay);
  }

  // Manual turns (low sensitivity): reply only to a transcript with words.
  _onUserTranscript(transcript) {
    if (!this.micPreset.manualTurns || !this.send) return;
    const text = (transcript || '').trim();
    if (text.length < MIN_TRANSCRIPT_CHARS) return; // noise — let the avatar carry on
    if (!this._bargedIn) this._interruptAvatar();   // short real reply ("yes") — still interrupt
    this.send({ type: 'response.create' });
  }

  // True between a tool call and the response that follows it — the gap in
  // which the visitor has heard (at most) the short lookup line.
  isAwaitingAnswer() {
    if (this._awaitingAnswerSince == null) return false;
    if (performance.now() - this._awaitingAnswerSince > 15000) {
      this._awaitingAnswerSince = null; // never block anything for long
      return false;
    }
    return true;
  }

  _startTurn() {
    this._turn = { t0: performance.now(), llmSent: false, e2eSent: false, audibleSent: false, responses: new Set() };
    this._respFirstDelta = {};
    this._toolResponses.clear();
    this.emitEvent('timing:turn_start');
  }

  _turnFor(responseId) {
    const t = this._turn;
    if (!t || !responseId || !t.responses.has(responseId)) return null;
    if (performance.now() - t.t0 > 60000) return null; // stale turn
    return t;
  }

  // First audio/text delta of a response.
  _onFirstDelta(responseId) {
    const key = responseId || '_';
    if (this._respFirstDelta[key] != null) return;
    const now = performance.now();
    this._respFirstDelta[key] = now;
    if (this._filler && this._filler.id === responseId) this._filler.speaking = true;
    const t = this._turnFor(responseId);
    if (t && !t.llmSent) {
      t.llmSent = true;
      // Time to first token from the end of the visitor's speech. Measured
      // from speech_stopped, not from the transcript: the transcript often
      // lands after the response has already started (→ ~0 ms before).
      this.emitEvent('timing:llm', Math.round(now - t.t0));
    }
  }

  // The visitor hears something: OpenAI audio starts playing (voice only) or
  // the avatar starts talking. e2e stops at the first token; this adds the
  // audio/avatar start-up on top. A "one moment" filler counts — it is what
  // the visitor hears first.
  markAudible(kind) {
    const t = this._turn;
    if (!t || t.audibleSent || performance.now() - t.t0 > 60000) return;
    t.audibleSent = true;
    const ms = Math.round(performance.now() - t.t0);
    console.log(`[timing] audible=${ms}ms (speech_stopped → ${kind} heard)`);
    this.emitEvent('timing:audible', ms);
  }

  _collectTranscript(responseId, text) {
    if (!text) return;
    const key = responseId || '_';
    this._respTranscripts[key] = this._respTranscripts[key]
      ? `${this._respTranscripts[key]} ${text}`
      : text;
  }

  _onResponseDone(response) {
    const id = response?.id || null;
    const key = id || '_';
    if (id && this._fillerIds.has(id)) {
      // Saved as interim like the old lookup line; the awaiting-answer state
      // from the tool call must survive it.
      this._fillerIds.delete(id);
      const words = this._respTranscripts[key];
      delete this._respTranscripts[key];
      delete this._respFirstDelta[key];
      if (words) this.emitEvent('transcript:assistant_interim', words);
      if (this._filler && this._filler.id === id) this._endFiller(); // not a newer one
      return;
    }
    const hadToolCall = this._toolResponses.has(key) ||
      !!(response?.output || []).some(o => o && o.type === 'function_call');
    if (hadToolCall) {
      this._awaitingAnswerSince = performance.now();
    } else {
      this._awaitingAnswerSince = null;
    }

    // e2e: speech_stopped → first delta of the response that actually answers,
    // i.e. the first one in the turn that did not end in a tool call.
    const t = this._turnFor(id);
    const firstDelta = this._respFirstDelta[key];
    const cancelled = response?.status === 'cancelled';
    if (t && !t.e2eSent && !hadToolCall && !cancelled && firstDelta != null) {
      t.e2eSent = true;
      const e2e = Math.round(firstDelta - t.t0);
      console.log(`[timing] e2e=${e2e}ms (speech_stopped → first answer delta)`);
      this.emitEvent('timing:e2e', e2e);
    }

    // Transcript is emitted here rather than on *.done so it is known whether
    // this response was only the lookup preamble ("Let me check.") — that one
    // is saved as interim and must not consume the turn's timings.
    const words = this._respTranscripts[key];
    delete this._respTranscripts[key];
    delete this._respFirstDelta[key];
    this._toolResponses.delete(key);
    if (words) this.emitEvent(hadToolCall ? 'transcript:assistant_interim' : 'transcript:assistant', words);
  }

  handleRealtimeEvent(event) {
    const data = JSON.parse(event.data);
    const ignoreLogs = [
      'response.output_audio_transcript.delta',
      'response.output_audio.done',
      'response.output_audio_transcript.done',
      'response.content_part.done',
      'response.output_item.done',
      'conversation.item.input_audio_transcription.delta',
      'conversation.item.input_audio_transcription.completed',
      'response.function_call_arguments.delta',
      'response.output_item.added',
      'input_audio_buffer.committed',
      'input_audio_buffer.cleared'
    ];
    if (!ignoreLogs.includes(data.type)) {
      console.log('Realtime Event:', data.type, data);
    }
    if (data.type === 'error') {
      console.error('[realtime] ERROR event:', JSON.stringify(data, null, 2));
      // A rejected filler request never gets a response.created — release the answer.
      if (this._filler && !this._filler.id) this._endFiller();
      this._cancelFiller = false;
    }
    switch (data.type) {
      case 'input_audio_buffer.speech_started':
        this._onSpeechStarted();
        this.emitEvent('voice:listening');
        this.emitEvent('user:speech_started');
        break;

      case 'input_audio_buffer.speech_stopped':
        clearTimeout(this._bargeInTimer);
        this._t_speechStop = performance.now();
        this._startTurn();
        this.emitEvent('voice:processing');
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (this._t_speechStop) {
          this.emitEvent('timing:asr', Math.round(performance.now() - this._t_speechStop));
          this._t_speechStop = null;
        }
        // Emit first: museum mode adds scene context to the conversation on
        // transcript:user, before a manual-turn reply is requested.
        this.emitEvent('transcript:user', data.transcript);
        this._onUserTranscript(data.transcript);
        break;

      case 'response.created':
        this._responseActive = true;
        if (data.response?.metadata?.purpose === 'filler') {
          // Not part of the turn: keeps it out of the llm/e2e timings.
          this._fillerIds.add(data.response.id);
          if (this._cancelFiller) {
            this._cancelFiller = false;
            this.send({ type: 'response.cancel', response_id: data.response.id });
          } else if (this._filler) {
            this._filler.id = data.response.id;
          }
          break;
        }
        if (this._turn && data.response?.id) this._turn.responses.add(data.response.id);
        break;

      // GA: response.output_audio.delta (beta: response.audio.delta)
      case 'response.output_audio.delta':
      case 'response.audio.delta':
        this._onFirstDelta(data.response_id);
        this.emitEvent('voice:speaking');
        break;

      // WebRTC-only lifecycle events for the remote audio track — in LITE
      // mode they gate what the bridge forwards to the avatar
      case 'output_audio_buffer.started':
        if (this.audioBridge) this.audioBridge.beginSpeech();
        if (!this.heygenManager) this.markAudible('voice');
        break;

      case 'output_audio_buffer.stopped':
        if (this.audioBridge) this.audioBridge.endSpeech();
        this.emitEvent('voice:done');
        this.emitEvent('voice:listening');
        break;

      case 'output_audio_buffer.cleared':
        if (this.audioBridge) this.audioBridge.abort();
        break;

      case 'response.output_audio.done':
      case 'response.audio.done':
        break;

      // GA: response.output_audio_transcript.done (beta: response.audio_transcript.done)
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
        // FULL avatar mode gets the assistant transcript from the text path;
        // no-avatar and LITE (audio output) modes get it from here
        if (!this.heygenManager || this.heygenManager.isLite) {
          this._collectTranscript(data.response_id, data.transcript);
        }
        break;

      // GA: response.output_text.delta (beta: response.text.delta)
      case 'response.output_text.delta':
      case 'response.text.delta':
        this._onFirstDelta(data.response_id);
        if (this.heygenManager) {
          // response_id lets the avatar drop deltas belonging to an answer the
          // user already interrupted, instead of resuming it.
          this.heygenManager.pushText(data.delta, data.response_id);
        }
        break;

      // GA: response.output_text.done (beta: response.text.done)
      case 'response.output_text.done':
      case 'response.text.done':
        if (this.heygenManager) {
          this.heygenManager.flushAll(data.response_id);
          this._collectTranscript(data.response_id, data.text);
        }
        break;

      case 'response.function_call_arguments.done':
        this._toolResponses.add(data.response_id || '_');
        this.toolHandler.handleToolCall(data);
        break;

      case 'response.done':
        this._responseActive = false;
        if (data.response?.usage) {
          this.statusDisplay.addUsage(data.response.usage);
          this.statusDisplay.showSummary();
          const u = data.response.usage;
          const itd = u.input_token_details || {};
          const otd = u.output_token_details || {};
          const inTxt       = itd.text_tokens  || 0;
          const cachedTxt   = itd.cached_tokens_details?.text_tokens  || 0;
          const cachedAudio = itd.cached_tokens_details?.audio_tokens || 0;
          const inAudio  = itd.audio_tokens || 0;
          const outTxt   = otd.text_tokens  || 0;
          const outAudio = otd.audio_tokens || 0;
          const t = this.statusDisplay.totalStats;
          console.log(
            `[tokens] response | in_text=${inTxt} cached_text=${cachedTxt} in_audio=${inAudio} cached_audio=${cachedAudio} out_text=${outTxt} out_audio=${outAudio} turn=${u.total_tokens}` +
            ` || cumulative text=${t.input_text + t.output_text} audio=${t.input_audio + t.output_audio} total=${t.total}`
          );
          const cc = this.statusDisplay.calculateCost(this.statusDisplay.currentStats);
          const ct = this.statusDisplay.calculateCost(t);
          console.log(
            `[priceplan] turn | ` +
            `audio_in=${inAudio - cachedAudio} cached_audio=${cachedAudio} audio_out=${outAudio} | ` +
            `text_in=${inTxt - cachedTxt} cached_text=${cachedTxt} text_out=${outTxt} | ` +
            `turn_cost=$${cc.totalCost.toFixed(5)} (audio=$${cc.costAudio.toFixed(5)} text=$${cc.costText.toFixed(5)}) | ` +
            `session_total=$${ct.totalCost.toFixed(5)} audio_tok=${t.input_audio + t.output_audio} text_tok=${t.input_text + t.output_text}`
          );
          if (outAudio > 0) {
            this.emitEvent('audio:seconds', outAudio / 100);
          }
        }
        this.lastMsgObj = data || {};
        this._onResponseDone(data.response);
        break;

      // GA: response.output_audio_transcript.delta (beta: response.audio_transcript.delta)
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        this.emitEvent('audio:delta');
        this.lastMsgObj = data || {};
        break;
    }
  }
}

export default RealtimeEventHandler;
